import './clubLogoBanner.css';
import '../../platform/static/tenantConfigs.css';

import React, { useEffect, useRef, useState } from 'react';

import { getTenantLogo, getTenantLogoBackgroundColor, getTenantLogoBorderRadius, getTenantLogoShadow } from '../../platform/static/tenantConfigs';


interface BannerLogo {
  tenant: string;
  sizeMultiplier?: number;
}


const logos: BannerLogo[] = [
  {
    tenant: 'glimt',
  },
  {
    tenant: 'rbk',
    sizeMultiplier: 1.3,
  },
  {
    tenant: 'djurgarden',
  },
  {
    tenant: 'viking',
    sizeMultiplier: 0.9,
  },
  {
    tenant: 'agf',
  },
  {
    tenant: 'brann',
    sizeMultiplier: 1.3,
  },
  {
    tenant: 'lyngby',
  },
  {
    tenant: 'vif',
  },
  {
    tenant: 'bromma',
  },
  {
    tenant: 'til',
  },
  {
    tenant: 'b93',
  },
  {
    tenant: 'fredrikstad',
  },
  {
    tenant: 'godset',
  },
  {
    tenant: 'sarpsborg',
    sizeMultiplier: 1.05,
  },
  {
    tenant: 'lsk',
  },
  {
    tenant: 'stb',
    sizeMultiplier: 1.05,
  },
  {
    tenant: 'aalesund',
  },
  {
    tenant: 'sandefjord',
  },
  {
    tenant: 'kbk',
  },
  {
    tenant: 'bryne',
  },
  {
    tenant: 'haugesund',
    sizeMultiplier: 0.95,
  },
  {
    tenant: 'odd',
  },
  {
    tenant: 'lyn',
  },
  {
    tenant: 'start',
  },
  {
    tenant: 'kongsvinger',
  },
  {
    tenant: 'ranheim',
    sizeMultiplier: 1.2,
  },
  {
    tenant: 'moss',
  },
  {
    tenant: 'sogndal',
  },
  {
    tenant: 'egersund',
    sizeMultiplier: 1.1,
  },
  {
    tenant: 'asane',
    sizeMultiplier: 0.8,
  },
  {
    tenant: 'hodd',
  },
  {
    tenant: 'skeid',
  },
];


interface ClubLogoBannerProps {
  width: number;
}


export const ClubLogoBanner: React.FC<ClubLogoBannerProps> = ({
  width,
}) => {

  const bannerRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [offset, setOffset] = useState(0);

  const [isResetting, setIsResetting] = useState(false);
  const [isResizing, setIsResizing] = useState(false);

  // we want to show at least 5 logos
  const numberOfLogosToShow = Math.max(5, Math.floor(width / 350));
  const logoContainerWidth = width / numberOfLogosToShow;


  // Clone logos to allow smooth infinite scroll
  const extendedLogos = [...logos, ...logos.slice(0, numberOfLogosToShow)];


  const scrollTransitionDuration = 500;
  const scrollInterval = 3000;


  // Scroll interval
  useEffect(() => {
    if (!isVisible || isResizing) return;

    const interval = setInterval(() => {
      setOffset((prevOffset) => {
        // Step 1: Animate to next offset
        const nextOffset = prevOffset + 1;

        // Step 2: Schedule reset after animation if we've reached clone zone
        if (nextOffset === logos.length) {
          setTimeout(() => {
            setIsResetting(true); // remove transition
            setOffset(0); // jump back to start
          }, scrollTransitionDuration);
        }

        return nextOffset;
      });
    }, scrollInterval);

    return () => clearInterval(interval);
  }, [isVisible, scrollTransitionDuration, isResizing]);


  // Watch visibility
  useEffect(() => {
    const element = bannerRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.5,
      }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, []);


  useEffect(() => {
    if (isResetting) {
      requestAnimationFrame(() => {
        setIsResetting(false);
      });
    }
  }, [isResetting]);


  useEffect(() => {
    setIsResizing(true);
  }, [width]);


  useEffect(() => {
    if (isResizing) {
      const timer = setTimeout(() => {
        setIsResizing(false);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [isResizing]);


  return (
    <div
      ref={bannerRef}
      className='club-logo-banner-container'
      style={{ width }}
    >
      <div
        className='club-logo-banner-slider'
        style={{
          transform: `translateX(-${offset * logoContainerWidth}px)`,
          transition: (isResetting || isResizing) ? 'none' : `transform ${scrollTransitionDuration}ms ease-in-out`,
          width: '100%',
        }}
      >
        {extendedLogos.map((logo, index) => {

          const desiredLogoWidth = 110 * (logo.sizeMultiplier ?? 1);
          const maxLogoWidth = logoContainerWidth - 15;
          const logoWidth = Math.min(desiredLogoWidth, maxLogoWidth);

          const logoShadow = getTenantLogoShadow(logo.tenant);
          const logoBackgroundColor = getTenantLogoBackgroundColor(logo.tenant);
          const logoBorderRadius = getTenantLogoBorderRadius(logo.tenant);

          return (
            <div
              key={index}
              className='club-logo-banner-logo-container'
              style={{
                minWidth: logoContainerWidth,
              }}
            >
              <div
                className='club-logo-banner-logo'
                style={{
                  width: logoWidth,
                  height: logoWidth * 0.7,
                }}
              >
                {logoShadow && <div className={'club-logo-banner-logo-shadow-overlay' + ' logo-shadow-' + logoShadow} />}

                <img
                  src={getTenantLogo(logo.tenant, true)}
                  alt='logo'
                  draggable={false}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    backgroundColor: logoBackgroundColor,
                    borderRadius: logoBorderRadius ?? (logoBackgroundColor ? 8 : undefined),
                    overflow: logoBorderRadius !== undefined ? 'hidden' : undefined,
                    padding: logoBackgroundColor ? '5%' : undefined,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
